import React, { useState, useCallback, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { List, Box, Typography, CircularProgress } from "@material-ui/core"
import { CornerUpRight } from "lucide-react"
import { toast } from "react-toastify"
import Modal from "../Modal"
import DefaultButton from "../DefaultButton"
import useStyles from "./styles"
import { useBuscaContatos } from "./hooks/useBuscaContatos"
import { ContactItem } from "../ContactList/components/ContactItem/ContactItem"
import FiltroNovaConversa from "../NovaConversaModal/components/filtroNovaConversa"
import { getChaveContatoLista, getTipoContato, isMesmoContato } from "../../helpers/tipoContato"

const ListaDeContatos = ({ mContatos, lCarregando, fnCarregarMais, mSelecionados, fnAlternar, chipFilter }) => {
  const oClasses = useStyles()
  const refThrottle = useRef(false)

  const fnAoRolar = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      if (scrollHeight - (scrollTop + clientHeight) < 100 && !refThrottle.current) {
        refThrottle.current = true
        fnCarregarMais()
        setTimeout(() => (refThrottle.current = false), 500)
      }
    },
    [fnCarregarMais],
  )

  if (lCarregando && mContatos.length === 0) {
    return (
      <Box className={oClasses.centeredState}>
        <CircularProgress size={24} />
        <Typography>Carregando contatos...</Typography>
      </Box>
    )
  }

  if (!mContatos.length) {
    return <Box className={oClasses.emptyState}>Nenhum contato encontrado</Box>
  }

  return (
    <Box className={oClasses.listContainer} onScroll={fnAoRolar} role="list">
      <List disablePadding>
        {mContatos.map((oContatoRecord) => {
          const lEstaSelecionado = mSelecionados.some((c) => isMesmoContato(c, oContatoRecord))
          const cItemKey = getChaveContatoLista(oContatoRecord)

          return (
            <ContactItem
              key={cItemKey}
              contato={oContatoRecord}
              isSelected={lEstaSelecionado}
              onSelect={fnAlternar}
              multiplos={true}
            />
          )
        })}
        {lCarregando && mContatos.length > 0 && <Box className={oClasses.loadingMore}>Carregando mais...</Box>}
      </List>
    </Box>
  )
}

// --- Componente Principal ---
const ModalSeletorContato = ({
  cTitulo = "Encaminhar mensagem",
  lAberto,
  fnAoFechar = () => {},
  fnAoConfirmar,
  nMaxSelecoes = 5,
  lConfirmando = false,
  cLabelConfirmar = "Encaminhar",
  fnBuscarContatos,
  nLargura = 420,
}) => {
  const oClasses = useStyles()
  const [mSelecionados, setMSelecionados] = useState([])
  const [chipFilter, setChipFilter] = useState(0)

  // --- Hook simplificado ---
  const { mContatos, cTermoBusca, lCarregando, fnSetTermoBusca, fnCarregarMais } = useBuscaContatos(
    lAberto,
    fnBuscarContatos,
    chipFilter === 0 ? null : chipFilter,
  )

  const lSubmissaoEmAndamentoRef = useRef(false)

  useEffect(() => {
    if (!lAberto) {
      setMSelecionados([])
      fnSetTermoBusca("")
      setChipFilter(0)
      lSubmissaoEmAndamentoRef.current = false
    }
  }, [lAberto, fnSetTermoBusca])

  const fnAlternarSelecao = useCallback(
    (oContatoRecord) => {
      setMSelecionados((mPrev) => {
        const cTipoContato = getTipoContato(oContatoRecord)
        const lJaSelecionado = mPrev.some((c) => isMesmoContato(c, oContatoRecord))

        if (lJaSelecionado) {
          return mPrev.filter((c) => !isMesmoContato(c, oContatoRecord))
        }

        if (mPrev.length >= nMaxSelecoes) {
          toast.warn(`Selecione no máximo ${nMaxSelecoes} contatos.`)
          return mPrev
        }

        return [...mPrev, { ...oContatoRecord, tipo: cTipoContato }]
      })
    },
    [nMaxSelecoes],
  )

  const fnConfirmar = useCallback(
    async (e) => {
      if (lSubmissaoEmAndamentoRef.current || lConfirmando) {
        return
      }
      lSubmissaoEmAndamentoRef.current = true
      try {
        await fnAoConfirmar(mSelecionados, e)
        fnAoFechar()
      } catch (err) {
        console.error("Erro ao confirmar:", err)
      } finally {
        lSubmissaoEmAndamentoRef.current = false
      }
    },
    [mSelecionados, fnAoConfirmar, fnAoFechar, lConfirmando],
  )

  return (
    <Modal
      title={cTitulo}
      modalOpen={lAberto}
      onClose={fnAoFechar}
      width={nLargura}
      tipoModal="encaminhar"
      footer={
        <Box className={oClasses.footer}>
          <DefaultButton
            stylization="primary"
            onClick={fnConfirmar}
            disabled={lConfirmando || mSelecionados.length === 0}
            aria-label={cLabelConfirmar}
            className={oClasses.btnFullWidth}
            aria-busy={lConfirmando}
            startIcon={
              lConfirmando ? (
                <CircularProgress size={16} color="inherit" className={oClasses.buttonProgress} />
              ) : (
                <CornerUpRight size={16} />
              )
            }
          >
            {lConfirmando ? "Enviando..." : cLabelConfirmar}
          </DefaultButton>
        </Box>
      }
    >
      <Box className={oClasses.wrapper}>
        <FiltroNovaConversa
          setSearchParam={fnSetTermoBusca}
          searchParam={cTermoBusca}
          chipFilter={chipFilter}
          setChipFilter={setChipFilter}
        />
        <ListaDeContatos
          mContatos={mContatos}
          lCarregando={lCarregando}
          fnCarregarMais={fnCarregarMais}
          mSelecionados={mSelecionados}
          fnAlternar={fnAlternarSelecao}
          chipFilter={chipFilter}
        />
      </Box>
    </Modal>
  )
}

ModalSeletorContato.propTypes = {
  cTitulo: PropTypes.string,
  lAberto: PropTypes.bool.isRequired,
  fnAoFechar: PropTypes.func.isRequired,
  fnAoConfirmar: PropTypes.func.isRequired,
  nMaxSelecoes: PropTypes.number,
  lConfirmando: PropTypes.bool,
  cLabelConfirmar: PropTypes.string,
  nLargura: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fnBuscarContatos: PropTypes.func,
}
ModalSeletorContato.defaultProps = {
  fnBuscarContatos: null,
}

ListaDeContatos.propTypes = {
  mContatos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      number: PropTypes.string,
      profilePicUrl: PropTypes.string,
      isGroup: PropTypes.bool,
      canalTipoId: PropTypes.number,
    }),
  ).isRequired,
  lCarregando: PropTypes.bool.isRequired,
  fnCarregarMais: PropTypes.func.isRequired,
  mSelecionados: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
  ).isRequired,
  fnAlternar: PropTypes.func.isRequired,
  chipFilter: PropTypes.number.isRequired,
}

export default React.memo(ModalSeletorContato)
